import { useAuthenticationStore } from "../../stores/auth";
import { SubscriptionStatusTypes } from "../../utils/constants";

function isPasswordValidFormat(
  password,
  setPasswordError = null,
  setPasswordErrorMessage = null,
  returnError = false,
) {
  if (!returnError && (!setPasswordError || !setPasswordErrorMessage)) {
    throw "isPasswordValidFormat requires either functions to set errors or returnError must be True to receive error directly";
  }
  if (!password) {
    if (returnError) {
      return "Password is required.";
    }
    setPasswordError(true);
    setPasswordErrorMessage("Password is required.");
    return false;
  }

  if (password.length < 6) {
    if (returnError) {
      return "Password must be at least 6 characters long.";
    }
    setPasswordError(true);
    setPasswordErrorMessage("Password must be at least 6 characters long.");
    return false;
  }

  // Check if password contains at least one number and one letter
  const hasNumber = /[0-9]/.test(password);
  const hasLetter = /[a-zA-Z]/.test(password);

  if (!hasNumber || !hasLetter) {
    if (returnError) {
      return "Password must contain both letters and numbers.";
    }
    setPasswordError(true);
    setPasswordErrorMessage("Password must contain both letters and numbers.");
    return false;
  }
  if (returnError) {
    return "";
  }
  setPasswordError(false);
  setPasswordErrorMessage("");
  return true;
}

const checkIfIsIndependentUser = () => {
  const { user } = useAuthenticationStore();

  return user?.info?.subscriptionStatus === SubscriptionStatusTypes.Independent;
};

const checkUserHasRole = ({ user, role }) => {
  if (!user?.info?.activeContext) {
    return false;
  }
  const { contextType, roleAssignment } = user.info.activeContext;
  if (contextType === "role_assignment") {
    return roleAssignment.role.code === role;
  }
  return contextType === role;
};

const checkIfIsSuperuser = ({ user }) => {
  return checkUserHasRole({ user: user, role: "superuser" });
};

const checkIfIsStudent = ({ user }) => {
  return checkUserHasRole({ user: user, role: "student" });
};

const checkUserHasBetaAccess = () => {
  const { user } = useAuthenticationStore();

  return user.info?.hasBetaAccess === true;
};
const checkHasApprovedSubscription = () => {
  const { user } = useAuthenticationStore();
  if (!user?.info?.activeContext) {
    return false;
  }
  const { hasApprovedSubscription } = user.info.activeContext;

  return hasApprovedSubscription;
};

const getActiveRoleAssignment = () => {
  const { user } = useAuthenticationStore();
  return user?.info?.activeContext?.roleAssignment || null;
};

const getSchoolsForUser = () => {
  const roleAssignment = getActiveRoleAssignment();

  if (!roleAssignment) {
    return [];
  }

  if (roleAssignment.schoolGroup) {
    return roleAssignment.schoolGroup.schools || [];
  }

  if (roleAssignment.school) {
    return [roleAssignment.school];
  }

  return [];
};

const getSubjectsForUser = () => {
  const roleAssignment = getActiveRoleAssignment();

  return roleAssignment?.subjects || [];
};

const getEducationLevelsForUser = () => {
  const roleAssignment = getActiveRoleAssignment();

  return roleAssignment?.educationLevels || [];
};

const getYearGroupsForUser = () => {
  const educationLevels = getEducationLevelsForUser();

  const yearGroups = educationLevels.flatMap(
    (educationLevel) => educationLevel.yearGroups || [],
  );

  return Array.from(
    new Map(yearGroups.map((yearGroup) => [yearGroup.id, yearGroup])).values(),
  ).sort((a, b) => (a.yearGroup ?? 0) - (b.yearGroup ?? 0));
};
export default isPasswordValidFormat;
export {
  checkIfIsIndependentUser,
  checkUserHasRole,
  checkUserHasBetaAccess,
  checkIfIsSuperuser,
  checkHasApprovedSubscription,
  checkIfIsStudent,
  getSchoolsForUser,
  getEducationLevelsForUser,
  getYearGroupsForUser,
  getSubjectsForUser,
};
